import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import {
  fetchExhibitorPortalSummary,
  updateExhibitorPortalDetails,
  claimExhibitorCompPasses,
  lookupExhibitorPortal,
  swapExhibitorStaff,
  addExhibitorStaff,
  fetchExhibitorLeads,
  updateLeadNote,
  getPortalInvoiceDownloadUrl,
  getExhibitorLeadsCsvExportUrl,
  syncOfflineExhibitorLeadsBatch,
  ExhibitorPortalSummary,
  ExhibitorDelegate,
  ExhibitorLead,
  CompPassPayload,
} from '../lib/api';
import { triggerMobileHaptic } from '../lib/nativeMobile';
import { eventInfo } from '../data';
import {
  Building2,
  Clock,
  MapPin,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Printer,
  Mail,
  Globe,
  ShieldCheck,
  Send,
  RefreshCw,
  Search,
  ChevronRight,
  Info,
  Calendar,
  Users,
  LayoutGrid,
  Award,
  ArrowLeftRight,
  Lock,
  UserPlus,
  FileText,
  Download,
  Share2,
  Copy,
  Check,
  X,
} from 'lucide-react';
import {
  fetchMeetings,
  MeetingItem,
  PortalAttendee,
  PortalExhibitor,
} from '../lib/api';
import { ScheduleXMeetingCalendar } from '../components/meetings/ScheduleXMeetingCalendar';
import { RequestMeetingModal } from '../components/meetings/RequestMeetingModal';
import { MeetingListTab } from '../components/meetings/MeetingListTab';
import { PortalAttendeeDirectory } from '../components/portal/PortalAttendeeDirectory';
import { PortalExhibitorDirectory } from '../components/portal/PortalExhibitorDirectory';

const DIRECTORY_CATEGORIES = [
  'Agrochemicals',
  'Biostimulants & Biofertilizers',
  'Formulations & Active Ingredients',
  'Agricultural Machinery & Implements',
  'Precision Irrigation & Water Tech',
  'Drone & Robotics Tech',
  'Seeds & Hybrid Genetics',
  'Crop Protection & Nutrition',
  'Agri-Fintech & Digital Supply Chain',
  'Post-Harvest Packaging & Cold Chain',
];

function StallHandoverTimingsCard() {
  return (
    <div className="bg-[#FAF5EA] border border-[#E6C687] rounded-3xl p-6 sm:p-8 space-y-6 shadow-xs">
      <div className="flex items-center gap-2 text-[#785613]">
        <Award className="w-5 h-5 text-[#C5A059]" />
        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">
          Stall Handover Timings
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 text-xs">
        <div className="p-4 bg-white rounded-2xl border border-slate-200/90 shadow-xs space-y-1">
          <span className="text-slate-500 block text-[11px] font-medium">Raw Stalls (Bare Space):</span>
          <span className="font-bold text-slate-900 text-sm sm:text-base block">
            {eventInfo.stallHandoverRules.rawStallHandover}
          </span>
          <span className="text-[10px] sm:text-[11px] text-[#0A3D27] font-semibold block">
            Freight unloading dock access
          </span>
        </div>

        <div className="p-4 bg-white rounded-2xl border border-slate-200/90 shadow-xs space-y-1">
          <span className="text-slate-500 block text-[11px] font-medium">Prefabricated Shell Scheme:</span>
          <span className="font-bold text-slate-900 text-sm sm:text-base block">
            {eventInfo.stallHandoverRules.prefabShellHandover}
          </span>
          <span className="text-[10px] sm:text-[11px] text-[#785613] font-semibold block">
            Octanorm structure and electrics ready
          </span>
        </div>
      </div>

      <p className="text-[11px] sm:text-xs text-slate-600 leading-relaxed">
        {eventInfo.stallHandoverRules.taxNote} {eventInfo.stallHandoverRules.overseasInvoicingNote}
      </p>

      <div className="pt-4 border-t border-[#E6C687]/60 text-[11px] text-slate-500 flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
        <span>Organized by {eventInfo.organizer.legalEntity} • GSTIN: {eventInfo.organizer.gstin}</span>
        <span className="text-slate-400">Jio World Convention Centre, Pavilion 3</span>
      </div>
    </div>
  );
}

export function ExhibitorPortalPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const queryClient = useQueryClient();

  // Portal Navigation Tabs
  const [activePortalTab, setActivePortalTab] = useState<
    'meetings' | 'outreach' | 'networking' | 'booth_setup' | 'leads' | 'marketing_kit'
  >('booth_setup');
  const [copiedPromoText, setCopiedPromoText] = useState(false);

  // Form State
  const [fasciaText, setFasciaText] = useState('');
  const [directoryBio, setDirectoryBio] = useState('');
  const [directoryWebsite, setDirectoryWebsite] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [detailsSavedMessage, setDetailsSavedMessage] = useState<string | null>(null);

  // Complimentary Passes State (Dynamic based on allocatedPasses)
  const [passesForm, setPassesForm] = useState<CompPassPayload[]>([
    { fullName: '', designation: '', email: '', phone: '' },
    { fullName: '', designation: '', email: '', phone: '' },
  ]);
  const [claimSuccessMessage, setClaimSuccessMessage] = useState<string | null>(null);
  const [printPassTarget, setPrintPassTarget] = useState<ExhibitorDelegate | null>(null);

  // Lookup State
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupMessage, setLookupMessage] = useState<string | null>(null);
  const [manualToken, setManualToken] = useState('');

  // Meeting Modal State
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [targetUserForMeeting, setTargetUserForMeeting] = useState<any>(null);
  const [slotClickDateTime, setSlotClickDateTime] = useState<string | undefined>(undefined);

  // Staff Swap State
  const [swapModalOpen, setSwapModalOpen] = useState(false);
  const [swapTarget, setSwapTarget] = useState<ExhibitorDelegate | null>(null);
  const [swapForm, setSwapForm] = useState<CompPassPayload>({
    fullName: '',
    email: '',
    phone: '',
    designation: '',
  });

  // Add Staff State
  const [addStaffModalOpen, setAddStaffModalOpen] = useState(false);
  const [addStaffForm, setAddStaffForm] = useState<CompPassPayload>({
    fullName: '',
    email: '',
    phone: '',
    designation: '',
  });

  // Leads State
  const [leadsSearch, setLeadsSearch] = useState('');
  const [leadsTypeFilter, setLeadsTypeFilter] = useState('all');
  const [activeNoteModal, setActiveNoteModal] = useState<{
    open: boolean;
    interactionId: string;
    leadName: string;
    note: string;
  } | null>(null);

  // Step 6: Instant Booth Lead Capture & Offline Queue State
  const [scanBadgeInput, setScanBadgeInput] = useState('');
  const [scanLeadRating, setScanLeadRating] = useState<'hot' | 'warm' | 'cold'>('hot');
  const [scanLeadNote, setScanLeadNote] = useState('');
  const [capturingLead, setCapturingLead] = useState(false);
  const [leadCaptureFeedback, setLeadCaptureFeedback] = useState<{
    type: 'online' | 'offline' | 'error';
    message: string;
  } | null>(null);
  const [offlineQueuedLeads, setOfflineQueuedLeads] = useState<
    Array<{
      badgeCode: string;
      leadRating: 'hot' | 'warm' | 'cold';
      notes: string;
      scannedAt: string;
    }>
  >(() => {
    try {
      const raw = localStorage.getItem(`iiab_offline_exhibitor_leads_${token || 'default'}`);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [syncingOfflineLeads, setSyncingOfflineLeads] = useState(false);

  // Sync URL ?tab= parameter (used by MobileAppTabBar Mode B)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'leads') setActivePortalTab('leads');
    else if (tabParam === 'meetings') setActivePortalTab('meetings');
    else if (tabParam === 'passes' || tabParam === 'booth_setup') setActivePortalTab('booth_setup');
  }, [searchParams]);

  // Persist offline queued leads & auto-sync when online
  const saveOfflineLeadsQueue = (
    nextQueue: Array<{
      badgeCode: string;
      leadRating: 'hot' | 'warm' | 'cold';
      notes: string;
      scannedAt: string;
    }>
  ) => {
    setOfflineQueuedLeads(nextQueue);
    try {
      localStorage.setItem(
        `iiab_offline_exhibitor_leads_${token || 'default'}`,
        JSON.stringify(nextQueue)
      );
    } catch {
      // Ignore storage quota errors
    }
  };

  const handleSyncOfflineLeadsQueue = async () => {
    if (!token || offlineQueuedLeads.length === 0 || syncingOfflineLeads) return;
    setSyncingOfflineLeads(true);
    try {
      const res = await syncOfflineExhibitorLeadsBatch(token, offlineQueuedLeads);
      saveOfflineLeadsQueue([]);
      triggerMobileHaptic('success');
      setLeadCaptureFeedback({
        type: 'online',
        message: `Synced ${res.syncedCount} queued offline lead(s) to your booth directory.`,
      });
      queryClient.invalidateQueries({ queryKey: ['exhibitor-leads', token] });
    } catch (err: any) {
      setLeadCaptureFeedback({
        type: 'error',
        message: err?.message || 'Still offline — queued leads remain safely stored on this device.',
      });
    } finally {
      setSyncingOfflineLeads(false);
    }
  };

  useEffect(() => {
    const onOnline = () => {
      if (offlineQueuedLeads.length > 0 && token) {
        handleSyncOfflineLeadsQueue();
      }
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [offlineQueuedLeads, token]);

  const handleInstantCaptureLead = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanCode = scanBadgeInput.trim();
    if (!cleanCode || !token) return;

    const leadItem = {
      badgeCode: cleanCode,
      leadRating: scanLeadRating,
      notes: scanLeadNote.trim(),
      scannedAt: new Date().toISOString(),
    };

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const next = [...offlineQueuedLeads, leadItem];
      saveOfflineLeadsQueue(next);
      triggerMobileHaptic('warning');
      setScanBadgeInput('');
      setScanLeadNote('');
      setLeadCaptureFeedback({
        type: 'offline',
        message: `Offline Mode: Queued ${cleanCode} (${scanLeadRating.toUpperCase()}) locally. Will auto-sync on reconnect.`,
      });
      return;
    }

    setCapturingLead(true);
    try {
      const res = await syncOfflineExhibitorLeadsBatch(token, [leadItem]);
      triggerMobileHaptic('success');
      setScanBadgeInput('');
      setScanLeadNote('');
      const matched = res.leads?.[0];
      setLeadCaptureFeedback({
        type: 'online',
        message: matched
          ? `Captured ${matched.fullName} (${matched.companyName}) — ${scanLeadRating.toUpperCase()} lead!`
          : `Lead scan recorded for ${cleanCode}.`,
      });
      queryClient.invalidateQueries({ queryKey: ['exhibitor-leads', token] });
    } catch {
      // Network failure fallback -> queue offline automatically
      const next = [...offlineQueuedLeads, leadItem];
      saveOfflineLeadsQueue(next);
      triggerMobileHaptic('warning');
      setScanBadgeInput('');
      setScanLeadNote('');
      setLeadCaptureFeedback({
        type: 'offline',
        message: `Network unreachable: Saved ${cleanCode} to offline device queue (${next.length} pending).`,
      });
    } finally {
      setCapturingLead(false);
    }
  };

  // Fetch summary if token is present
  const {
    data: summary,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<ExhibitorPortalSummary>({
    queryKey: ['exhibitor-portal', token],
    queryFn: () => fetchExhibitorPortalSummary(token),
    enabled: !!token,
    retry: 1,
  });

  // Query B2B Meetings
  const { data: meetingsData } = useQuery({
    queryKey: ['meetings', summary?.reservationId],
    queryFn: () => fetchMeetings(summary!.reservationId),
    enabled: !!summary?.reservationId,
  });
  const meetings = meetingsData?.meetings || [];

  // Query Captured Leads
  const { data: leadsData } = useQuery({
    queryKey: ['exhibitor-leads', token],
    queryFn: () => fetchExhibitorLeads(token),
    enabled: !!token,
  });
  const leads = leadsData?.leads || [];

  // Sync initial state when summary loads
  useEffect(() => {
    if (summary) {
      setFasciaText(summary.fasciaName || summary.companyName.toUpperCase().slice(0, 30));
      setDirectoryBio(summary.directoryBio || '');
      setDirectoryWebsite(summary.directoryWebsite || '');
      setSelectedCategories(summary.directoryCategories || []);
      const count = Math.max(1, summary.allocatedPasses || 2);
      setPassesForm((prev) => {
        if (prev.length === count) return prev;
        return Array.from({ length: count }, (_, idx) => prev[idx] || {
          fullName: '',
          designation: '',
          email: '',
          phone: '',
        });
      });
    }
  }, [summary]);

  // Mutation: Details update
  const detailsMutation = useMutation({
    mutationFn: () =>
      updateExhibitorPortalDetails(token, {
        fasciaName: fasciaText.trim().toUpperCase(),
        directoryBio: directoryBio.trim(),
        directoryWebsite: directoryWebsite.trim(),
        directoryCategories: selectedCategories,
      }),
    onSuccess: (data) => {
      setDetailsSavedMessage(data.message || 'Exhibitor directory and fascia details saved successfully.');
      queryClient.invalidateQueries({ queryKey: ['exhibitor-portal', token] });
      setTimeout(() => setDetailsSavedMessage(null), 4000);
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to update details');
    },
  });

  // Mutation: Claim comp passes
  const claimMutation = useMutation({
    mutationFn: () =>
      claimExhibitorCompPasses(token, {
        passes: passesForm.filter((p) => p.fullName.trim() && p.email.trim()),
      }),
    onSuccess: (data) => {
      setClaimSuccessMessage(data.message || 'Complimentary exhibitor passes generated successfully!');
      queryClient.invalidateQueries({ queryKey: ['exhibitor-portal', token] });
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to claim passes');
    },
  });

  // Mutation: Swap Representative
  const swapMutation = useMutation({
    mutationFn: () => swapExhibitorStaff(token, swapTarget!.id, swapForm),
    onSuccess: (data) => {
      setSwapModalOpen(false);
      setSwapTarget(null);
      setSwapForm({ fullName: '', email: '', phone: '', designation: '' });
      setClaimSuccessMessage(data.message || 'Representative swapped successfully.');
      queryClient.invalidateQueries({ queryKey: ['exhibitor-portal', token] });
      setTimeout(() => setClaimSuccessMessage(null), 5000);
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to swap representative');
    },
  });

  // Mutation: Add Staff
  const addStaffMutation = useMutation({
    mutationFn: () => addExhibitorStaff(token, addStaffForm),
    onSuccess: (data) => {
      setAddStaffModalOpen(false);
      setAddStaffForm({ fullName: '', email: '', phone: '', designation: '' });
      setClaimSuccessMessage(data.message || 'Staff representative added successfully.');
      queryClient.invalidateQueries({ queryKey: ['exhibitor-portal', token] });
      setTimeout(() => setClaimSuccessMessage(null), 5000);
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to add representative');
    },
  });

  // Mutation: Save Note
  const noteMutation = useMutation({
    mutationFn: ({ interactionId, note }: { interactionId: string; note: string }) =>
      updateLeadNote(token, interactionId, note),
    onSuccess: () => {
      setActiveNoteModal(null);
      queryClient.invalidateQueries({ queryKey: ['exhibitor-leads', token] });
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to save lead note');
    },
  });

  // Mutation: Lookup by email
  const lookupMutation = useMutation({
    mutationFn: (email: string) => lookupExhibitorPortal(email),
    onSuccess: (data) => {
      setLookupMessage(data.message);
      if (data.token) {
        setSearchParams({ token: data.token });
      }
    },
    onError: (err: any) => {
      alert(err.message || 'Lookup failed');
    },
  });

  const toggleCategory = (cat: string) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const handleLookupSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (lookupEmail.trim()) {
      lookupMutation.mutate(lookupEmail.trim());
    }
  };

  const handleManualTokenSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualToken.trim()) {
      setSearchParams({ token: manualToken.trim() });
    }
  };

  const handleDetailsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    detailsMutation.mutate();
  };

  const handlePassesSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    claimMutation.mutate();
  };

  // Filter leads
  const filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      lead.fullName.toLowerCase().includes(leadsSearch.toLowerCase()) ||
      lead.companyName.toLowerCase().includes(leadsSearch.toLowerCase()) ||
      lead.jobTitle.toLowerCase().includes(leadsSearch.toLowerCase());

    const matchesType =
      leadsTypeFilter === 'all' || lead.interactionType === leadsTypeFilter;

    return matchesSearch && matchesType;
  });

  // -------------------------------------------------------------
  // VIEW: NO TOKEN ENTERED
  // -------------------------------------------------------------
  if (!token) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 space-y-8 animate-in fade-in duration-300">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold bg-[#FAF5EA] text-[#785613] border border-[#E6C687]">
            <Building2 className="w-3.5 h-3.5" />
            <span>Exhibitor Self-Service Portal</span>
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">
            Manage Your Exhibition Stall
          </h1>
          <p className="text-sm text-slate-600 max-w-lg mx-auto">
            Access your fascia setup, directory listing, complimentary exhibitor badges, captured leads, and B2B schedule.
          </p>
        </div>

        {/* Card: Lookup by Email */}
        <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 space-y-6 shadow-sm">
          <div className="space-y-1">
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#0A3D27]" />
              <span>Retrieve Your Portal Link</span>
            </h2>
            <p className="text-xs text-slate-500">
              Enter the primary email address used when booking your exhibition stall.
            </p>
          </div>

          {lookupMessage ? (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl space-y-2 text-xs text-[#0A3D27] animate-in fade-in">
              <div className="flex items-center gap-2 font-bold">
                <CheckCircle2 className="w-4 h-4 text-[#0A3D27]" />
                <span>Request Processed</span>
              </div>
              <p>{lookupMessage}</p>
            </div>
          ) : (
            <form onSubmit={handleLookupSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Registered Exhibitor Email</label>
                <input
                  type="email"
                  required
                  placeholder="contact@yourcompany.com"
                  value={lookupEmail}
                  onChange={(e) => setLookupEmail(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                />
              </div>

              <button
                type="submit"
                disabled={lookupMutation.isPending}
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-emerald-950/20 cursor-pointer disabled:opacity-50"
              >
                {lookupMutation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Searching Registry...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Send Access Link</span>
                  </>
                )}
              </button>
            </form>
          )}

          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-[10px] uppercase font-bold text-slate-400">
              <span className="bg-white px-3">or enter portal token directly</span>
            </div>
          </div>

          <form onSubmit={handleManualTokenSubmit} className="flex gap-2">
            <input
              type="text"
              placeholder="Paste 36-character portal token..."
              value={manualToken}
              onChange={(e) => setManualToken(e.target.value)}
              className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-900 placeholder-slate-400 font-mono focus:outline-none focus:border-[#0A3D27] focus:bg-white"
            />
            <button
              type="submit"
              className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs rounded-xl transition-colors cursor-pointer"
            >
              Verify
            </button>
          </form>
        </div>

        <StallHandoverTimingsCard />

        <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-100">
          <Link to="/" className="hover:text-[#0A3D27] transition-colors">
            &larr; Return to Home
          </Link>
          <Link to="/floorplan" className="text-[#0A3D27] hover:underline font-semibold flex items-center gap-1">
            <span>Book New Stall (Floor Plan)</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // VIEW: LOADING
  // -------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 text-center space-y-4">
        <RefreshCw className="w-8 h-8 text-[#0A3D27] animate-spin mx-auto" />
        <h2 className="text-base font-bold text-slate-900">Loading Exhibitor Stall Dashboard...</h2>
        <p className="text-xs text-slate-500">Decrypting reservation credentials and directory assets</p>
      </div>
    );
  }

  // -------------------------------------------------------------
  // VIEW: ERROR / NOT FOUND
  // -------------------------------------------------------------
  if (isError || !summary) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center space-y-6">
        <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-200 text-amber-700 flex items-center justify-center mx-auto">
          <AlertCircle className="w-6 h-6" />
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-black text-slate-900">Portal Access Denied</h2>
          <p className="text-xs text-slate-600">
            {(error as any)?.message || 'This portal link is invalid, cancelled, or has expired.'}
          </p>
        </div>
        <div className="pt-2 flex flex-col gap-2">
          <button
            onClick={() => setSearchParams({})}
            className="w-full py-2.5 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition-colors"
          >
            Lookup by Registered Email
          </button>
          <Link to="/" className="text-xs text-slate-500 hover:text-slate-800 py-1">
            Return to Homepage
          </Link>
        </div>
      </div>
    );
  }

  const isSwapLocked = summary.swapLock?.isLocked || false;
  const allocatedPasses = summary.allocatedPasses || 2;
  const activeClaimedCount =
    summary.activeClaimedCount ?? summary.delegates?.filter((d) => !d.isSwapped).length ?? 0;

  const handleDownloadPromoGraphic = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 630;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Deep Emerald Gradient Background
    const grad = ctx.createLinearGradient(0, 0, 1200, 630);
    grad.addColorStop(0, '#0A3D27');
    grad.addColorStop(0.65, '#062B1B');
    grad.addColorStop(1, '#041A10');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 1200, 630);

    // Gold Accent Border
    ctx.strokeStyle = '#E6C687';
    ctx.lineWidth = 4;
    ctx.strokeRect(36, 36, 1128, 558);

    // Top Event Pill
    ctx.fillStyle = '#E6C687';
    ctx.font = 'bold 20px system-ui, -apple-system, sans-serif';
    ctx.fillText('INDIA INTERNATIONAL AGRI BUSINESS CONFERENCE & EXPO • 5TH EDITION', 76, 102);

    // Stall Number Badge
    ctx.fillStyle = '#FAF5EA';
    ctx.fillRect(76, 134, 340, 68);
    ctx.fillStyle = '#0A3D27';
    ctx.font = '900 32px system-ui, -apple-system, sans-serif';
    ctx.fillText(`VISIT STALL ${summary.boothId}`, 102, 179);

    // Exhibitor Company Name
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '900 54px system-ui, -apple-system, sans-serif';
    const displayCompany = (summary.fasciaName || summary.companyName).slice(0, 34);
    ctx.fillText(displayCompany, 76, 290);

    // Tagline
    ctx.fillStyle = '#D1FAE5';
    ctx.font = '500 26px system-ui, -apple-system, sans-serif';
    ctx.fillText(
      `Meet our leadership team & explore our solutions at Stall ${summary.boothId} (${summary.boothSize})`,
      76,
      348
    );

    // Divider Line
    ctx.strokeStyle = 'rgba(230, 198, 135, 0.35)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(76, 415);
    ctx.lineTo(1124, 415);
    ctx.stroke();

    // Event Dates & Venue Footer
    ctx.fillStyle = '#FAF5EA';
    ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
    ctx.fillText('📅 27–28 July 2027  •  10:00 AM – 06:00 PM', 76, 475);

    ctx.fillStyle = '#E6C687';
    ctx.font = 'bold 24px system-ui, -apple-system, sans-serif';
    ctx.fillText('📍 Jio World Convention Centre (JWCC), Pavilion 3, BKC, Mumbai', 76, 522);

    ctx.fillStyle = '#94A3B8';
    ctx.font = '600 18px monospace';
    ctx.fillText('Register & Schedule B2B Meetings at iiabexpo.com', 76, 562);

    const dataUrl = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `IIAB2027_Stall_${summary.boothId}_Promo_Banner.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // -------------------------------------------------------------
  // VIEW: MAIN EXHIBITOR DASHBOARD
  // -------------------------------------------------------------
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8 animate-in fade-in duration-300">
      {/* HEADER / BOOTH PROFILE SUMMARY */}
      <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 space-y-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-3 py-1 rounded-full text-xs font-black bg-emerald-50 text-[#0A3D27] border border-emerald-200">
                STALL {summary.boothId}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
                {summary.boothSize} • {summary.areaSqm} sqm
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#FAF5EA] text-[#785613] border border-[#E6C687]">
                BOOKED & CONFIRMED
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
              {summary.companyName}
            </h1>

            <p className="text-xs text-slate-600 flex items-center gap-2 flex-wrap">
              <span>Contact: <strong>{summary.contactPerson || 'Exhibitor Contact'}</strong></span>
              <span>•</span>
              <span>Email: <strong>{summary.email}</strong></span>
              {summary.phone && (
                <>
                  <span>•</span>
                  <span>Phone: <strong>{summary.phone}</strong></span>
                </>
              )}
            </p>

            <div className="pt-1 flex flex-wrap items-center gap-2.5">
              <a
                href={getPortalInvoiceDownloadUrl(summary.reservationId, token)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] text-xs font-bold shadow-xs transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                <span>
                  {summary.zohoInvoiceNumber
                    ? `Download GST Tax Invoice (${summary.zohoInvoiceNumber})`
                    : 'Download GST Tax Invoice'}
                </span>
              </a>

              <button
                type="button"
                onClick={() => setActivePortalTab('marketing_kit')}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#FAF5EA] hover:bg-[#F3E8CE] border border-[#E6C687] text-[#785613] text-xs font-bold transition-colors cursor-pointer"
              >
                <Share2 className="w-3.5 h-3.5" />
                <span>Stall Promo Banner &amp; Kit</span>
              </button>
            </div>
          </div>

          <div className="bg-[#F8F9F6] border border-slate-200 p-4 rounded-2xl space-y-2.5 min-w-[240px]">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Stall Handover Schedule
            </div>
            <div className="flex items-center gap-2 text-xs font-semibold text-[#0A3D27]">
              <Clock className="w-4 h-4 shrink-0" />
              <span>{summary.handoverTime}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <MapPin className="w-4 h-4 shrink-0 text-slate-400" />
              <span className="truncate">{eventInfo.venue.name}, {eventInfo.venue.city}</span>
            </div>
            <Link
              to="/floorplan"
              className="mt-2 pt-2 border-t border-slate-200 text-[11px] font-bold text-[#0A3D27] hover:underline flex items-center justify-between group"
            >
              <span>Book Additional Stall</span>
              <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </div>

      {detailsSavedMessage && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-xs text-[#0A3D27] flex items-center gap-3 animate-in fade-in">
          <CheckCircle2 className="w-5 h-5 text-[#0A3D27] shrink-0" />
          <span>{detailsSavedMessage}</span>
        </div>
      )}

      {claimSuccessMessage && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-xs text-[#0A3D27] flex items-center gap-3 animate-in fade-in">
          <CheckCircle2 className="w-5 h-5 text-[#0A3D27] shrink-0" />
          <span>{claimSuccessMessage}</span>
        </div>
      )}

      {/* PORTAL NAVIGATION TABS */}
      <div className="flex border-b border-slate-200 gap-2 overflow-x-auto">
        <button
          type="button"
          onClick={() => setActivePortalTab('booth_setup')}
          className={`pb-3 px-4 text-xs font-bold border-b-2 flex items-center gap-2 cursor-pointer transition-colors whitespace-nowrap ${
            activePortalTab === 'booth_setup'
              ? 'border-[#0A3D27] text-[#0A3D27]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          <span>Booth Setup & Passes ({activeClaimedCount}/{allocatedPasses})</span>
        </button>

        <button
          type="button"
          onClick={() => setActivePortalTab('leads')}
          className={`pb-3 px-4 text-xs font-bold border-b-2 flex items-center gap-2 cursor-pointer transition-colors whitespace-nowrap ${
            activePortalTab === 'leads'
              ? 'border-[#0A3D27] text-[#0A3D27]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Users className="w-4 h-4" />
          <span>Captured Leads ({leads.length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActivePortalTab('meetings')}
          className={`pb-3 px-4 text-xs font-bold border-b-2 flex items-center gap-2 cursor-pointer transition-colors whitespace-nowrap ${
            activePortalTab === 'meetings'
              ? 'border-[#0A3D27] text-[#0A3D27]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Calendar className="w-4 h-4" />
          <span>B2B Meeting Center ({meetings.length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActivePortalTab('outreach')}
          className={`pb-3 px-4 text-xs font-bold border-b-2 flex items-center gap-2 cursor-pointer transition-colors whitespace-nowrap ${
            activePortalTab === 'outreach'
              ? 'border-[#0A3D27] text-[#0A3D27]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Search className="w-4 h-4" />
          <span>Find Clients / Delegates</span>
        </button>

        <button
          type="button"
          onClick={() => setActivePortalTab('networking')}
          className={`pb-3 px-4 text-xs font-bold border-b-2 flex items-center gap-2 cursor-pointer transition-colors whitespace-nowrap ${
            activePortalTab === 'networking'
              ? 'border-[#0A3D27] text-[#0A3D27]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Building2 className="w-4 h-4" />
          <span>Exhibitor Networking</span>
        </button>

        <button
          type="button"
          onClick={() => setActivePortalTab('marketing_kit')}
          className={`pb-3 px-4 text-xs font-bold border-b-2 flex items-center gap-2 cursor-pointer transition-colors whitespace-nowrap ${
            activePortalTab === 'marketing_kit'
              ? 'border-[#0A3D27] text-[#0A3D27]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Share2 className="w-4 h-4" />
          <span>Marketing &amp; Promo Kit</span>
        </button>
      </div>

      {/* ============================================================= */}
      {/* TAB 1: BOOTH SETUP & PASSES */}
      {/* ============================================================= */}
      {activePortalTab === 'booth_setup' && (
        <div className="space-y-8 animate-in fade-in">
          {/* SECTION 1: FASCIA NAME SPECIFICATION */}
          <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 space-y-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-[#FAF5EA] border border-[#E6C687] text-[#785613] flex items-center justify-center font-bold text-xs">
                1
              </div>
              <div>
                <h2 className="text-lg font-black text-slate-900">Stall Fascia Name Board</h2>
                <p className="text-xs text-slate-500">
                  Printed in uppercase lettering above your octanorm stall frame (max 30 characters).
                </p>
              </div>
            </div>

            <form onSubmit={handleDetailsSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Fascia Board Text (Exact Print)</label>
                <div className="relative">
                  <input
                    type="text"
                    maxLength={30}
                    value={fasciaText}
                    onChange={(e) => setFasciaText(e.target.value.toUpperCase())}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-black text-slate-900 tracking-wider uppercase focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                  />
                  <span className="absolute right-3.5 top-3.5 text-[11px] font-mono text-slate-400">
                    {fasciaText.length}/30
                  </span>
                </div>
              </div>

              {/* SECTION 2: OFFICIAL DIRECTORY LISTING */}
              <div className="pt-6 border-t border-slate-100 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-[#FAF5EA] border border-[#E6C687] text-[#785613] flex items-center justify-center font-bold text-xs">
                    2
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Official Expo Directory Listing</h3>
                    <p className="text-xs text-slate-500">
                      Published in the IIAB 2027 Official Show Guide and delegate matchmaking search.
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-700">Official Website</label>
                    <div className="relative">
                      <Globe className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                      <input
                        type="url"
                        placeholder="https://www.yourcompany.com"
                        value={directoryWebsite}
                        onChange={(e) => setDirectoryWebsite(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-700">Company Bio & Core Offerings</label>
                    <textarea
                      rows={3}
                      maxLength={1200}
                      placeholder="Briefly describe your company, product lines, and what you are showcasing at Pavilion 3..."
                      value={directoryBio}
                      onChange={(e) => setDirectoryBio(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27] focus:bg-white resize-none"
                    />
                    <span className="text-[10px] text-slate-400 float-right font-mono">
                      {directoryBio.length}/1200
                    </span>
                  </div>

                  <div className="space-y-2 pt-2">
                    <label className="text-xs font-semibold text-slate-700 block">
                      Select Industry Categories
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {DIRECTORY_CATEGORIES.map((cat) => {
                        const isSelected = selectedCategories.includes(cat);
                        return (
                          <button
                            type="button"
                            key={cat}
                            onClick={() => toggleCategory(cat)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer ${
                              isSelected
                                ? 'bg-[#0A3D27] text-white'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                          >
                            {cat}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4 flex justify-end">
                <button
                  type="submit"
                  disabled={detailsMutation.isPending}
                  className="px-6 py-2.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-emerald-950/20 cursor-pointer disabled:opacity-50"
                >
                  {detailsMutation.isPending ? 'Saving...' : 'Save Fascia & Directory Profile'}
                </button>
              </div>
            </form>
          </div>

          {/* SECTION 3: EXHIBITOR BADGES & STAFF SWAP LOCK */}
          <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 space-y-6 shadow-sm">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-emerald-50 border border-emerald-200 text-[#0A3D27] flex items-center justify-center">
                    <ShieldCheck className="w-4 h-4" />
                  </div>
                  <h2 className="text-lg font-black text-slate-900">
                    3. Stall Staff Representatives ({activeClaimedCount}/{allocatedPasses})
                  </h2>
                </div>
                <p className="text-xs text-slate-500">
                  Each stall includes {allocatedPasses} all-access official exhibitor badges with lead capture capability.
                </p>
              </div>

              {summary.compPassesClaimed && (
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 border border-emerald-200 text-[#0A3D27] flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Badges Provisioned</span>
                </span>
              )}
            </div>

            {/* SWAP LOCK STATUS BANNER */}
            {isSwapLocked ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3 text-xs text-amber-900 animate-in fade-in">
                <Lock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <h4 className="font-bold text-amber-950">Staff Swaps Locked</h4>
                  <p className="text-amber-800">
                    Self-service staff representative swaps are closed as the event cutoff has passed (
                    {summary.swapLock?.formattedCutoff}). For on-site emergency changes, please visit the Registration Counter.
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-emerald-50/80 border border-emerald-200 rounded-2xl flex items-start justify-between gap-4 text-xs text-[#0A3D27] animate-in fade-in">
                <div className="flex items-start gap-3">
                  <ArrowLeftRight className="w-5 h-5 text-[#0A3D27] shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    <h4 className="font-bold">Staff Swaps Available</h4>
                    <p className="text-slate-600">
                      You may add or swap named stall representatives anytime until the cutoff:{' '}
                      <strong className="text-slate-900 font-semibold">{summary.swapLock?.formattedCutoff}</strong>.
                    </p>
                  </div>
                </div>
                {activeClaimedCount < allocatedPasses && (
                  <button
                    type="button"
                    onClick={() => setAddStaffModalOpen(true)}
                    className="px-3 py-1.5 bg-[#0A3D27] text-white rounded-lg text-xs font-bold hover:bg-[#062B1B] transition-colors shrink-0 flex items-center gap-1.5 cursor-pointer"
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    <span>Add Representative</span>
                  </button>
                )}
              </div>
            )}

            {/* DISPLAY CLAIMED DELEGATES */}
            {summary.compPassesClaimed && summary.delegates && summary.delegates.length > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                  {summary.delegates.map((delegate, idx) => {
                    const isSwapped = delegate.isSwapped || delegate.status === 'swapped';
                    return (
                      <div
                        key={delegate.id || idx}
                        className={`rounded-2xl p-6 space-y-4 border transition-all ${
                          isSwapped
                            ? 'bg-slate-50 border-slate-200 opacity-60'
                            : 'bg-[#F8F9F6] border-slate-200 hover:border-[#0A3D27]/40 shadow-xs'
                        }`}
                      >
                        <div className="flex items-center justify-between border-b border-slate-200/80 pb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-bold text-purple-900 uppercase tracking-wider">
                              Pass #{idx + 1}
                            </span>
                            {isSwapped ? (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-200 text-slate-600">
                                Swapped Out / Cancelled
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-[#0A3D27]">
                                Active
                              </span>
                            )}
                          </div>
                          {delegate.badgeCode && (
                            <span className="font-mono text-[10px] font-bold text-[#0A3D27] bg-white px-2 py-0.5 rounded-md border border-slate-200">
                              {delegate.badgeCode}
                            </span>
                          )}
                        </div>

                        <div className="flex items-start gap-4">
                          {!isSwapped && (
                            <div className="bg-white p-2 rounded-xl border border-slate-200 shrink-0 shadow-xs">
                              <QRCodeSVG
                                value={delegate.qrToken || 'IIAB-EXHIBITOR'}
                                size={80}
                                level="M"
                                marginSize={1}
                                fgColor="#0A3D27"
                              />
                            </div>
                          )}

                          <div className="space-y-1 text-xs flex-1">
                            <h4 className="font-bold text-slate-900 text-base leading-tight">
                              {delegate.fullName}
                            </h4>
                            <p className="text-purple-900 font-semibold">
                              {delegate.designation || 'Exhibitor Representative'}
                            </p>
                            <p className="text-slate-600 truncate">{delegate.email}</p>
                            <p className="text-slate-500 font-mono text-[11px]">{delegate.phone}</p>
                          </div>
                        </div>

                        <div className="pt-2 border-t border-slate-200 flex items-center justify-between text-xs">
                          <span className="text-slate-500 text-[11px]">Gate: Express Gate 3</span>
                          <div className="flex items-center gap-3">
                            {!isSwapped && (
                              <button
                                type="button"
                                disabled={isSwapLocked}
                                onClick={() => {
                                  setSwapTarget(delegate);
                                  setSwapForm({
                                    fullName: '',
                                    email: '',
                                    phone: '',
                                    designation: '',
                                  });
                                  setSwapModalOpen(true);
                                }}
                                className={`text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer ${
                                  isSwapLocked
                                    ? 'text-slate-400 cursor-not-allowed'
                                    : 'text-[#0A3D27] hover:underline'
                                }`}
                                title={isSwapLocked ? 'Swaps locked as cutoff has passed' : 'Swap representative'}
                              >
                                <ArrowLeftRight className="w-3.5 h-3.5" />
                                <span>Swap Rep</span>
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => setPrintPassTarget(delegate)}
                              className="text-slate-600 hover:text-slate-900 flex items-center gap-1 cursor-pointer text-[11px] font-semibold"
                            >
                              <Printer className="w-3.5 h-3.5" />
                              <span>Print 4×6 Badge</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* INITIAL CLAIM FORM IF NOT YET CLAIMED (Dynamic up to allocatedPasses) */
              <form onSubmit={handlePassesSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {passesForm.map((passEntry, idx) => (
                    <div
                      key={idx}
                      className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4"
                    >
                      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-emerald-100 text-[#0A3D27] font-bold text-xs flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                            Exhibitor Representative {idx + 1}
                          </h3>
                        </div>
                        <span className="text-[10px] font-semibold text-slate-400">
                          {idx === 0 ? 'Required' : 'Included in Stall Quota'}
                        </span>
                      </div>

                      <div className="space-y-3">
                        <div className="space-y-1">
                          <label className="text-[11px] font-semibold text-slate-700">
                            Full Name {idx === 0 ? '*' : ''}
                          </label>
                          <input
                            type="text"
                            required={idx === 0}
                            placeholder={idx === 0 ? 'e.g. Rajesh Kumar' : 'e.g. Priya Nair'}
                            value={passEntry.fullName}
                            onChange={(e) => {
                              const next = [...passesForm];
                              next[idx] = { ...next[idx], fullName: e.target.value };
                              setPassesForm(next);
                            }}
                            className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="text-[11px] font-semibold text-slate-700">
                            Designation {idx === 0 ? '*' : ''}
                          </label>
                          <input
                            type="text"
                            required={idx === 0}
                            placeholder={idx === 0 ? 'e.g. Managing Director' : 'e.g. Technical Director'}
                            value={passEntry.designation}
                            onChange={(e) => {
                              const next = [...passesForm];
                              next[idx] = { ...next[idx], designation: e.target.value };
                              setPassesForm(next);
                            }}
                            className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="text-[11px] font-semibold text-slate-700">
                            Email Address {idx === 0 ? '*' : ''}
                          </label>
                          <input
                            type="email"
                            required={idx === 0}
                            placeholder={idx === 0 ? 'rajesh@company.com' : 'priya@company.com'}
                            value={passEntry.email}
                            onChange={(e) => {
                              const next = [...passesForm];
                              next[idx] = { ...next[idx], email: e.target.value };
                              setPassesForm(next);
                            }}
                            className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="text-[11px] font-semibold text-slate-700">
                            Phone Number {idx === 0 ? '*' : ''}
                          </label>
                          <input
                            type="tel"
                            required={idx === 0}
                            placeholder="+91 98000 11111"
                            value={passEntry.phone}
                            onChange={(e) => {
                              const next = [...passesForm];
                              next[idx] = { ...next[idx], phone: e.target.value };
                              setPassesForm(next);
                            }}
                            className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-2 flex-wrap gap-4">
                  <span className="text-[11px] text-slate-500">
                    Badges will be emailed immediately with encrypted entry tokens and badge codes.
                  </span>
                  <button
                    type="submit"
                    disabled={claimMutation.isPending}
                    className="flex items-center gap-2 px-8 py-3.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-black transition-all shadow-md shadow-emerald-950/20 cursor-pointer disabled:opacity-50"
                  >
                    {claimMutation.isPending ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Generating Official Badges...</span>
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="w-4 h-4" />
                        <span>
                          Generate {passesForm.filter((p) => p.fullName.trim()).length || allocatedPasses} Exhibitor Badges
                        </span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          <StallHandoverTimingsCard />
        </div>
      )}

      {/* ============================================================= */}
      {/* TAB 2: CAPTURED LEADS (IN-PLATFORM VIEWER) */}
      {/* ============================================================= */}
      {activePortalTab === 'leads' && (
        <div className="space-y-6 animate-in fade-in">
          {/* Header & Security Notice */}
          <div className="bg-[#FAF5EA] border border-[#E6C687] rounded-3xl p-6 sm:p-8 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-[#785613]">
                  <ShieldCheck className="w-5 h-5 text-[#C5A059]" />
                  <span className="text-xs font-bold uppercase tracking-wider">
                    In-Platform Lead Directory
                  </span>
                </div>
                <h2 className="text-xl font-black text-slate-900 mt-1">
                  Visitor Leads Captured ({leads.length})
                </h2>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <a
                  href={getExhibitorLeadsCsvExportUrl(token)}
                  download
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] text-xs font-bold shadow-sm transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Leads CSV ({leads.length})</span>
                </a>

                <div className="bg-white px-4 py-2 rounded-2xl border border-[#E6C687] text-xs space-y-0.5">
                  <span className="text-slate-500 text-[10px] block uppercase font-bold">Stall Leads</span>
                  <span className="text-lg font-black text-[#0A3D27]">{leads.length} Total</span>
                </div>
              </div>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed">
              🔒 <strong>Verified Exhibitor Lead Export:</strong> Delegate leads captured via QR badge scans, NFC badge taps, and profile exchanges are available inside your portal and can be exported to CSV for your CRM. Contact details for visitors who opted out of directory sharing remain privacy-masked in accordance with DPDP regulations.
            </p>
          </div>

          {/* Step 6: Instant Booth Lead Scanner & Offline Sync Queue Card */}
          <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-black text-slate-900">
                  Instant Booth Lead Capture (Online &amp; Offline-Resilient)
                </h3>
                <p className="text-xs text-slate-500">
                  Scan or enter a visitor&apos;s badge code (`IIAB27-...` or 6-char code). If Pavilion 3 Wi-Fi drops, scans are queued on this device and auto-synced upon reconnect.
                </p>
              </div>
              {offlineQueuedLeads.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-bold border border-amber-300">
                    ⚡ {offlineQueuedLeads.length} Offline Lead(s) Queued
                  </span>
                  <button
                    type="button"
                    disabled={syncingOfflineLeads}
                    onClick={handleSyncOfflineLeadsQueue}
                    className="px-3.5 py-1.5 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-white text-xs font-bold cursor-pointer disabled:opacity-50"
                  >
                    {syncingOfflineLeads ? 'Syncing...' : 'Sync Now'}
                  </button>
                </div>
              )}
            </div>

            {leadCaptureFeedback && (
              <div
                className={`p-3 rounded-xl text-xs font-semibold flex items-center justify-between ${
                  leadCaptureFeedback.type === 'online'
                    ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
                    : leadCaptureFeedback.type === 'offline'
                    ? 'bg-amber-50 text-amber-900 border border-amber-200'
                    : 'bg-rose-50 text-rose-900 border border-rose-200'
                }`}
              >
                <span>{leadCaptureFeedback.message}</span>
                <button
                  type="button"
                  onClick={() => setLeadCaptureFeedback(null)}
                  className="text-slate-500 hover:text-slate-800 ml-2 font-bold"
                >
                  ×
                </button>
              </div>
            )}

            <form onSubmit={handleInstantCaptureLead} className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-4">
                <input
                  type="text"
                  required
                  placeholder="Badge Code (e.g., IIAB27-... or 6-char)"
                  value={scanBadgeInput}
                  onChange={(e) => setScanBadgeInput(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 font-mono focus:outline-none focus:border-[#0A3D27]"
                />
              </div>
              <div className="md:col-span-3 flex items-center gap-1.5">
                {(['hot', 'warm', 'cold'] as const).map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setScanLeadRating(rating)}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-bold capitalize border transition-colors cursor-pointer ${
                      scanLeadRating === rating
                        ? rating === 'hot'
                          ? 'bg-rose-600 text-white border-rose-600'
                          : rating === 'warm'
                          ? 'bg-amber-500 text-white border-amber-500'
                          : 'bg-sky-600 text-white border-sky-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {rating === 'hot' ? '🔥 Hot' : rating === 'warm' ? '⚡ Warm' : '❄️ Cold'}
                  </button>
                ))}
              </div>
              <div className="md:col-span-3">
                <input
                  type="text"
                  placeholder="Quick booth note (e.g., Wants 500L quote)"
                  value={scanLeadNote}
                  onChange={(e) => setScanLeadNote(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>
              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={capturingLead}
                  className="w-full py-2.5 px-4 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] text-xs font-bold cursor-pointer transition-colors disabled:opacity-50"
                >
                  {capturingLead ? 'Saving...' : 'Capture Lead'}
                </button>
              </div>
            </form>
          </div>

          {/* Search & Filter Toolbar */}
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="relative flex-1 w-full">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
              <input
                type="text"
                placeholder="Search leads by name, company, or job title..."
                value={leadsSearch}
                onChange={(e) => setLeadsSearch(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27]"
              />
            </div>

            <select
              value={leadsTypeFilter}
              onChange={(e) => setLeadsTypeFilter(e.target.value)}
              className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs text-slate-700 focus:outline-none focus:border-[#0A3D27] w-full sm:w-auto cursor-pointer"
            >
              <option value="all">All Interaction Types</option>
              <option value="badge_scan_qr">QR Badge Scans</option>
              <option value="badge_tap_nfc">NFC Badge Taps</option>
              <option value="meeting_requested">Meeting Requests</option>
              <option value="contact_saved">Contact Exchanges</option>
            </select>
          </div>

          {/* Leads Grid */}
          {filteredLeads.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center space-y-3">
              <Users className="w-8 h-8 text-slate-300 mx-auto" />
              <h3 className="text-base font-bold text-slate-900">No Captured Leads Found</h3>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">
                {leads.length === 0
                  ? 'As visitors and delegates tap their badges or scan your booth QR, their profiles and contact details will appear here automatically.'
                  : 'No leads matched your search query or filter criteria.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredLeads.map((lead) => (
                <div
                  key={lead.id}
                  className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4 hover:border-[#0A3D27]/40 transition-all shadow-xs"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 text-[#0A3D27] flex items-center justify-center font-black text-sm">
                        {lead.fullName.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 text-sm">{lead.fullName}</h4>
                        <p className="text-xs text-slate-500">{lead.jobTitle} • {lead.companyName}</p>
                      </div>
                    </div>

                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 capitalize">
                      {lead.tier.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="space-y-1.5 text-xs border-t border-slate-100 pt-3">
                    <div className="flex items-center justify-between text-slate-600">
                      <span className="text-[11px] text-slate-400">Email:</span>
                      <span className="font-mono">{lead.email}</span>
                    </div>
                    <div className="flex items-center justify-between text-slate-600">
                      <span className="text-[11px] text-slate-400">Phone:</span>
                      <span className="font-mono">{lead.phone}</span>
                    </div>
                    {lead.isContactPrivate && (
                      <div className="text-[10px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200/60 inline-block">
                        Visitor opted out of public directory; masked for privacy
                      </div>
                    )}
                  </div>

                  {lead.note && (
                    <div className="p-2.5 bg-[#FAF5EA] border border-[#E6C687]/60 rounded-xl text-xs text-[#785613] space-y-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider block">Staff Note:</span>
                      <p>{lead.note}</p>
                    </div>
                  )}

                  <div className="border-t border-slate-100 pt-3 flex items-center justify-between text-[11px] text-slate-400">
                    <span>
                      {lead.interactionType.replace(/_/g, ' ')} •{' '}
                      {new Date(lead.capturedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setActiveNoteModal({
                          open: true,
                          interactionId: lead.id,
                          leadName: lead.fullName,
                          note: lead.note || '',
                        })
                      }
                      className="text-[#0A3D27] font-bold hover:underline cursor-pointer flex items-center gap-1"
                    >
                      <FileText className="w-3 h-3" />
                      <span>{lead.note ? 'Edit Note' : 'Add Note'}</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ============================================================= */}
      {/* TAB 3: B2B MEETING CENTER */}
      {/* ============================================================= */}
      {activePortalTab === 'meetings' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Stall {summary.boothId} Calendar & Meetings</h2>
              <p className="text-xs text-slate-500">
                Manage B2B discussions with buyers, delegates, and fellow exhibitors during expo hours (27–28 July 2027).
              </p>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
            <ScheduleXMeetingCalendar
              meetings={meetings}
              onSlotClick={(startDateTime) => {
                setSlotClickDateTime(startDateTime);
                setActivePortalTab('outreach');
              }}
            />
          </div>

          <MeetingListTab
            meetings={meetings}
            currentUserId={summary.reservationId}
          />
        </div>
      )}

      {/* ============================================================= */}
      {/* TAB 4: OUTREACH & ATTENDEE DIRECTORY */}
      {/* ============================================================= */}
      {activePortalTab === 'outreach' && (
        <div className="space-y-6">
          <PortalAttendeeDirectory
            currentUserId={summary.reservationId}
            onScheduleMeeting={(targetUser: PortalAttendee) => {
              setTargetUserForMeeting({
                type: 'attendee',
                id: targetUser.id,
                fullName: targetUser.fullName,
                name: targetUser.fullName,
                companyName: targetUser.companyName || '',
                org: targetUser.companyName || targetUser.jobTitle || 'Trade Visitor',
                jobTitle: targetUser.jobTitle || '',
                tier: targetUser.tier || 'trade_visitor',
                email: (targetUser as any).email || 'attendee@iiab-delegate.org',
              });
              setIsMeetingModalOpen(true);
            }}
          />
        </div>
      )}

      {/* ============================================================= */}
      {/* TAB 5: EXHIBITOR NETWORKING */}
      {/* ============================================================= */}
      {activePortalTab === 'networking' && (
        <div className="space-y-6">
          <PortalExhibitorDirectory
            onScheduleMeeting={(targetExhibitor: PortalExhibitor) => {
              setTargetUserForMeeting({
                type: 'exhibitor',
                id: targetExhibitor.reservationId || (targetExhibitor as any).id,
                fullName: targetExhibitor.fasciaName || targetExhibitor.companyName,
                name: targetExhibitor.fasciaName || targetExhibitor.companyName,
                companyName: targetExhibitor.companyName,
                org: targetExhibitor.companyName,
                jobTitle: `Stall ${targetExhibitor.boothId}`,
                tier: 'exhibitor',
                email: targetExhibitor.contactEmail,
                stallNumber: targetExhibitor.boothId,
              });
              setIsMeetingModalOpen(true);
            }}
          />
        </div>
      )}

      {/* ============================================================= */}
      {/* TAB 6: MARKETING & PROMO KIT (Orig-B3) */}
      {/* ============================================================= */}
      {activePortalTab === 'marketing_kit' && (
        <div className="space-y-6 animate-in fade-in">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 space-y-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FAF5EA] border border-[#E6C687] text-[#785613] text-[11px] font-bold uppercase tracking-wider">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Exhibitor Co-Marketing Suite</span>
                </span>
                <h2 className="text-xl font-black text-slate-900 mt-2">
                  Custom Stall Promotional Graphic &amp; Social Kit
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Download your personalized 1200×630 banner to invite buyers, distributors, and partners on LinkedIn, WhatsApp, and email.
                </p>
              </div>

              <button
                type="button"
                onClick={handleDownloadPromoGraphic}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-[#FAF5EA] text-xs font-black shadow-md transition-all cursor-pointer shrink-0"
              >
                <Download className="w-4 h-4" />
                <span>Download Promo Graphic (PNG)</span>
              </button>
            </div>

            {/* Live 1200x630 Banner Preview */}
            <div className="rounded-2xl overflow-hidden border-2 border-[#E6C687] bg-gradient-to-br from-[#0A3D27] via-[#062B1B] to-[#041A10] p-6 sm:p-10 text-white shadow-xl relative">
              <div className="border border-[#E6C687]/50 rounded-xl p-6 sm:p-8 space-y-5">
                <div className="text-[11px] sm:text-xs font-bold tracking-[0.18em] text-[#E6C687] uppercase">
                  India International Agri Business Conference &amp; Expo • 5th Edition
                </div>

                <div>
                  <span className="inline-block px-4 py-1.5 rounded-lg bg-[#FAF5EA] text-[#0A3D27] font-black text-sm sm:text-base tracking-wide">
                    VISIT STALL {summary.boothId}
                  </span>
                </div>

                <div className="space-y-1.5">
                  <h3 className="text-2xl sm:text-4xl font-black tracking-tight text-white">
                    {summary.fasciaName || summary.companyName}
                  </h3>
                  <p className="text-xs sm:text-sm text-emerald-100/90">
                    Meet our leadership team &amp; explore our solutions at Stall {summary.boothId} ({summary.boothSize})
                  </p>
                </div>

                <div className="pt-4 border-t border-[#E6C687]/30 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                  <div className="font-bold text-[#FAF5EA]">
                    📅 27–28 July 2027 • 10:00 AM – 06:00 PM
                  </div>
                  <div className="font-semibold text-[#E6C687]">
                    📍 Jio World Convention Centre (JWCC), Pavilion 3, BKC, Mumbai
                  </div>
                </div>
              </div>
            </div>

            {/* Ready-to-Share Social Copy */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Ready-to-Post LinkedIn &amp; WhatsApp Invitation Copy
                </h4>
                <button
                  type="button"
                  onClick={() => {
                    const text = `🌿 Join ${summary.companyName} at the 5th Edition of the India International Agri Business Conference & Expo (IIAB 2027)!\n\n📍 Visit us at Stall ${summary.boothId} (${summary.boothSize})\n🗓️ 27–28 July 2027 | 10:00 AM – 06:00 PM\n🏢 Jio World Convention Centre (JWCC), Pavilion 3, BKC, Mumbai\n\nRegister your pass & book a 1-on-1 B2B meeting with our team at ${window.location.origin}/register`;
                    navigator.clipboard.writeText(text);
                    setCopiedPromoText(true);
                    setTimeout(() => setCopiedPromoText(false), 2500);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white hover:bg-slate-100 border border-slate-300 text-slate-800 text-xs font-bold transition-colors cursor-pointer"
                >
                  {copiedPromoText ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-[#0A3D27]" />
                      <span className="text-[#0A3D27]">Copied to Clipboard!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copy Invitation Copy</span>
                    </>
                  )}
                </button>
              </div>
              <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans bg-white p-3.5 rounded-xl border border-slate-200">
{`🌿 Join ${summary.companyName} at the 5th Edition of the India International Agri Business Conference & Expo (IIAB 2027)!

📍 Visit us at Stall ${summary.boothId} (${summary.boothSize})
🗓️ 27–28 July 2027 | 10:00 AM – 06:00 PM
🏢 Jio World Convention Centre (JWCC), Pavilion 3, BKC, Mumbai

Register your pass & book a 1-on-1 B2B meeting with our team at ${window.location.origin}/register`}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================= */}
      {/* MODAL: SWAP REPRESENTATIVE */}
      {/* ============================================================= */}
      {swapModalOpen && swapTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 max-w-lg w-full space-y-6 shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="space-y-0.5">
                <span className="text-[10px] font-bold text-[#785613] uppercase tracking-wider">
                  Stall {summary.boothId} • Personnel Swap
                </span>
                <h3 className="text-lg font-black text-slate-900">
                  Swap Representative
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSwapModalOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-2xl text-xs text-amber-900 space-y-1">
              <div className="font-bold flex items-center gap-1.5">
                <ArrowLeftRight className="w-3.5 h-3.5 text-amber-700" />
                <span>Replacing: {swapTarget.fullName}</span>
              </div>
              <p className="text-[11px] text-amber-800">
                This action will mark {swapTarget.fullName}&apos;s badge as cancelled and revoke their gate entry token. A fresh active badge credential will be issued to the new representative.
              </p>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                swapMutation.mutate();
              }}
              className="space-y-4"
            >
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Replacement Full Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Anand Sharma"
                  value={swapForm.fullName}
                  onChange={(e) => setSwapForm({ ...swapForm, fullName: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Official Work Email *</label>
                <input
                  type="email"
                  required
                  placeholder="anand@company.com"
                  value={swapForm.email}
                  onChange={(e) => setSwapForm({ ...swapForm, email: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-700">Mobile Phone *</label>
                  <input
                    type="tel"
                    required
                    placeholder="+91 98000 55555"
                    value={swapForm.phone}
                    onChange={(e) => setSwapForm({ ...swapForm, phone: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-700">Designation</label>
                  <input
                    type="text"
                    placeholder="e.g. Regional Sales Head"
                    value={swapForm.designation}
                    onChange={(e) => setSwapForm({ ...swapForm, designation: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                  />
                </div>
              </div>

              <div className="pt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setSwapModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={swapMutation.isPending}
                  className="px-6 py-2.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-emerald-950/20 cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                >
                  {swapMutation.isPending ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Swapping...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span>Confirm Swap</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================================= */}
      {/* MODAL: ADD REPRESENTATIVE */}
      {/* ============================================================= */}
      {addStaffModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 max-w-lg w-full space-y-6 shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="space-y-0.5">
                <span className="text-[10px] font-bold text-[#0A3D27] uppercase tracking-wider">
                  Stall {summary.boothId} • Personnel Allocation
                </span>
                <h3 className="text-lg font-black text-slate-900">
                  Add Additional Stall Representative
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setAddStaffModalOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                addStaffMutation.mutate();
              }}
              className="space-y-4"
            >
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Full Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Ramesh Patel"
                  value={addStaffForm.fullName}
                  onChange={(e) => setAddStaffForm({ ...addStaffForm, fullName: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700">Email Address *</label>
                <input
                  type="email"
                  required
                  placeholder="ramesh@company.com"
                  value={addStaffForm.email}
                  onChange={(e) => setAddStaffForm({ ...addStaffForm, email: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-700">Phone Number *</label>
                  <input
                    type="tel"
                    required
                    placeholder="+91 98000 66666"
                    value={addStaffForm.phone}
                    onChange={(e) => setAddStaffForm({ ...addStaffForm, phone: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-700">Designation</label>
                  <input
                    type="text"
                    placeholder="e.g. Agronomist"
                    value={addStaffForm.designation}
                    onChange={(e) => setAddStaffForm({ ...addStaffForm, designation: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] focus:bg-white"
                  />
                </div>
              </div>

              <div className="pt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setAddStaffModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addStaffMutation.isPending}
                  className="px-6 py-2.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-emerald-950/20 cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                >
                  {addStaffMutation.isPending ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-3.5 h-3.5" />
                      <span>Add Representative</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================================= */}
      {/* MODAL: ADD / EDIT LEAD NOTE */}
      {/* ============================================================= */}
      {activeNoteModal && activeNoteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 max-w-md w-full space-y-5 shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <span className="text-[10px] font-bold text-[#0A3D27] uppercase tracking-wider">
                  Stall Lead Note
                </span>
                <h3 className="text-base font-bold text-slate-900">
                  {activeNoteModal.leadName}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setActiveNoteModal(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-700">Follow-Up Note / Requirements</label>
              <textarea
                rows={4}
                maxLength={1000}
                placeholder="e.g. Interested in 500 MT organic biostimulants. Send quotation by 30 July..."
                value={activeNoteModal.note}
                onChange={(e) =>
                  setActiveNoteModal({ ...activeNoteModal, note: e.target.value })
                }
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] focus:bg-white resize-none"
              />
              <span className="text-[10px] text-slate-400 float-right font-mono">
                {activeNoteModal.note.length}/1000
              </span>
            </div>

            <div className="pt-3 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setActiveNoteModal(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={noteMutation.isPending}
                onClick={() =>
                  noteMutation.mutate({
                    interactionId: activeNoteModal.interactionId,
                    note: activeNoteModal.note,
                  })
                }
                className="px-6 py-2.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-emerald-950/20 cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
              >
                {noteMutation.isPending ? 'Saving...' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: 4x6 PHYSICAL BADGE PRINT PREVIEW */}
      {printPassTarget && summary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/75 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-md w-full space-y-5 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3 print:hidden">
              <div>
                <span className="text-[10px] font-bold text-[#0A3D27] uppercase tracking-wider">
                  4&quot; × 6&quot; Badge Print Preview
                </span>
                <h3 className="text-base font-black text-slate-900">
                  {printPassTarget.fullName}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setPrintPassTarget(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* 4x6 Physical Badge Card Container */}
            <div
              id="exhibitor-badge-4x6-print"
              className="mx-auto w-[320px] min-h-[480px] rounded-2xl border-2 border-slate-900 bg-white overflow-hidden flex flex-col justify-between shadow-lg"
            >
              {/* Top Header */}
              <div className="bg-[#0A3D27] text-white px-5 py-4 text-center space-y-1">
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#E6C687]">
                  27–28 July 2027 · JWCC Mumbai
                </div>
                <div className="text-sm font-black tracking-tight">
                  IIAB 2027 EXPO &amp; CONFERENCE
                </div>
                <div className="text-[10px] text-emerald-200">
                  Jio World Convention Centre · Pavilion 3
                </div>
              </div>

              {/* Center Identity & QR */}
              <div className="px-6 py-5 flex-1 flex flex-col items-center justify-center text-center space-y-3">
                <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-sky-100 text-sky-900 border border-sky-300">
                  Stall {summary.boothId} Representative
                </span>

                <div className="space-y-1">
                  <h4 className="text-xl font-black text-slate-900 leading-tight">
                    {printPassTarget.fullName}
                  </h4>
                  <p className="text-xs font-bold text-slate-600">
                    {printPassTarget.designation || 'Exhibitor Representative'}
                  </p>
                  <p className="text-sm font-black text-[#0A3D27] uppercase tracking-wide pt-0.5">
                    {summary.fasciaName || summary.companyName}
                  </p>
                </div>

                <div className="p-3 bg-white rounded-2xl border-2 border-slate-200 shadow-xs my-1">
                  <QRCodeSVG
                    value={printPassTarget.qrToken || printPassTarget.badgeCode || 'IIAB-EXHIBITOR'}
                    size={128}
                    level="M"
                    marginSize={1}
                    fgColor="#0A3D27"
                  />
                </div>

                <div className="font-mono text-xs font-black tracking-widest text-slate-800 bg-slate-100 px-3 py-1 rounded-lg border border-slate-200">
                  {printPassTarget.badgeCode || 'EXH-PASS'}
                </div>
              </div>

              {/* Bottom Role Color Band */}
              <div className="bg-sky-700 text-white py-3 px-4 text-center">
                <div className="text-sm font-black tracking-[0.25em] uppercase">
                  EXHIBITOR
                </div>
                <div className="text-[9px] font-semibold tracking-wider uppercase text-sky-200">
                  Express Gate 3 Access · Lead Capture Enabled
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2 print:hidden">
              <button
                type="button"
                onClick={() => setPrintPassTarget(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => window.print()}
                className="px-5 py-2.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-md cursor-pointer"
              >
                <Printer className="w-4 h-4" />
                <span>Print 4&quot; × 6&quot; Badge</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MEETING BOOKING MODAL */}
      {summary && targetUserForMeeting && (
        <RequestMeetingModal
          isOpen={isMeetingModalOpen}
          onClose={() => {
            setIsMeetingModalOpen(false);
            setTargetUserForMeeting(null);
          }}
          currentUser={{
            type: 'exhibitor',
            id: summary.reservationId,
            name: summary.fasciaName || summary.companyName,
            org: summary.companyName,
            email: summary.email,
            stallNumber: summary.boothId,
          }}
          targetUser={targetUserForMeeting}
          initialDateTime={slotClickDateTime}
        />
      )}

      {/* Footer Support Notice */}
      <div className="text-center text-xs text-slate-500 pt-4 space-y-1 border-t border-slate-200">
        <div>IIAB Secretariat Exhibitor Operations Desk • Jio World Convention Centre (JWCC), Pavilion 3, Mumbai</div>
        <div>
          Need additional utilities or extra badges? Email{' '}
          <a href="mailto:support@iiab-expo.org" className="text-[#0A3D27] hover:underline font-semibold">
            support@iiab-expo.org
          </a>
        </div>
      </div>
    </div>
  );
}

export default ExhibitorPortalPage;
